import { Injectable } from '@angular/core';
import { PointerModel } from 'models/generated/Distribution.Api.Models.Pointer';
import { SiteModel } from 'models/generated/Distribution.Api.Models.Site';
import punycode from 'punycode';
import { DnsInformationModel } from 'services/domain.service';
import { SiteService } from './site.service';

interface IPunyCode { encode: (value: string) => string; decode: (value: string) => string; toASCII: (value: string) => string; toUnicode: (value: string) => string; }
interface IDohJs { DohResolver: new (url: string) => { query: (qname: string, qtype?: string, method?: string, headers?: object, timeout?: number) => Promise<IDnsResponse> } };
interface IDnsResponse {
	status?: number;
	answers?: { name: string, type: string, ttl: number, data: DnsData }[];
}
type DnsData = string | { mname: string; } | undefined;
declare const doh: IDohJs;

@Injectable()
export class DnsLookupService {

	public async aRecordLookup(hostname: string): Promise<string> {
		return (await this.dnsLookup<string | undefined>(hostname, 'A')) ?? '';
	}

	public async soaLookup(hostname: string): Promise<DnsInformationModel> {
		return {
			name: hostname,
			ipAddress: await this.dnsLookup<string | undefined>(hostname, 'A'),
			nameServer: (await this.dnsLookup<{ mname: string; } | undefined>(hostname, 'SOA'))?.mname
		};
	}

	private async dnsLookup<T extends DnsData>(hostname: string, queryType: string): Promise<T> {
		const resolver = new doh.DohResolver('https://1.1.1.1/dns-query');
		const response = await resolver.query(punycode.toASCII(hostname), queryType);
		return response.answers?.find(answer => answer.type === queryType)?.data as T;
	}
}

@Injectable()
export class DnsPointerService {

	private validIp = new Map<string, boolean>(); // Pointer status icon based on the pointer id, in case multiple sites have the same pointer name
	private dnsResponse = new Map<string, string>(); // DNS response based on just the pointer name
	private dnsRequest = new Map<string, Promise<string>>(); // DNS response based on just the pointer name

	constructor(private readonly siteService: SiteService, private readonly dnsLookupService: DnsLookupService) { }

	private ensureDnsLookup(site: SiteModel, pointer: PointerModel): void {
		if (this.dnsRequest.has(pointer.id!)) return;
		this.dnsRequest.set(pointer.id!, this.dnsLookupService.aRecordLookup(pointer.name).then(result => {
			this.dnsResponse.set(pointer.name, result);
			this.setPointerStatus(site);
			return result;
		}));
	}

	private setPointerStatus(site: SiteModel): void {

		let matchDns = '';
		for (const pointer of site.pointers) {
			if (!this.siteService.isTestAddress(pointer.name)) continue; // Use first matching test address as "master" for the IP necessary for online domains
			const pointerIp = this.dnsResponse.get(pointer.name);
			if (!pointerIp) continue;

			matchDns = pointerIp;
			break;
		}
		if (matchDns == '') return;

		for (const pointer of site.pointers) {
			if (this.siteService.isTestAddress(pointer.name)) {
				this.validIp.set(pointer.id!, true); // Test addresses are always valid
				continue;
			}
			const pointerIp = this.dnsResponse.get(pointer.name);
			if (!pointerIp) continue;

			// Same hostname can exist on multiple sites and only be valid on one of them, so validation is based on the pointer id
			this.validIp.set(pointer.id!, pointerIp == matchDns);
		}
	}

	public getValidPointers(site: SiteModel): string[] {
		const pointers: string[] = [];
		for (const pointer of site.pointers) {
			this.ensureDnsLookup(site, pointer);
			if (this.siteService.isTestAddress(pointer.name) || this.validIp.get(pointer.id!)) {
				pointers.push(pointer.name);
			}
		}
		return pointers;
	}

	public getStatusColor(site: SiteModel, pointer: PointerModel): string {
		this.ensureDnsLookup(site, pointer);
		return this.validIp.has(pointer.id!) ?
			(this.validIp.get(pointer.id!) ? 'color-mint-70' : 'color-red-50') :
			(this.siteService.isTestAddress(pointer.name) ? 'color-mint-70' : '');
	}
	public getStatusText(site: SiteModel, pointer: PointerModel): string {
		this.ensureDnsLookup(site, pointer);
		return this.dnsResponse.get(pointer.name) ?? '';
	}
}
